import { useConnection, useWallet } from "@solana/wallet-adapter-react"
import type {
  AddressLookupTableAccount,
  TransactionError,
} from "@solana/web3.js"
import {
  PublicKey,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js"
import { useCallback } from "react"
import {
  parseSvmTransactionError,
  TransactionRevertedError,
} from "../lib/errors"
import type { TransactionReceipt } from "../types"

type Instruction = {
  keys: Array<{
    pubkey: string
    isSigner: boolean
    isWritable: boolean
  }>
  programId: string
  data: string | null
}

export const useSvmTransact = () => {
  const { connection } = useConnection()
  const { wallet } = useWallet()

  const transact = useCallback(
    async ({
      instructions,
      addressLookupTableAddresses,
    }: {
      instructions: Instruction[]
      addressLookupTableAddresses: string[]
    }) => {
      if (!wallet?.adapter.publicKey) {
        throw new Error("Wallet not connected")
      }

      try {
        const {
          context: { slot: minContextSlot },
          value: { blockhash, lastValidBlockHeight },
        } = await connection.getLatestBlockhashAndContext()

        const transactionInstructions = instructions.map(
          instruction =>
            new TransactionInstruction({
              keys: instruction.keys.map(key => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              programId: new PublicKey(instruction.programId),
              data: instruction.data
                ? Buffer.from(instruction.data, "hex")
                : undefined,
            }),
        )

        const addressLookupTableAccounts = await Promise.all(
          addressLookupTableAddresses.map(address =>
            connection
              .getAddressLookupTable(new PublicKey(address))
              .then(res => res.value as AddressLookupTableAccount),
          ),
        )

        const message = new TransactionMessage({
          payerKey: wallet.adapter.publicKey,
          recentBlockhash: blockhash,
          instructions: transactionInstructions,
        })

        const transaction = new VersionedTransaction(
          message.compileToV0Message(addressLookupTableAccounts),
        )

        const signature = await wallet.adapter.sendTransaction(
          transaction,
          connection,
          {
            minContextSlot,
          },
        )

        const wait = async (): Promise<TransactionReceipt> => {
          try {
            const receipt = await connection.confirmTransaction({
              blockhash,
              lastValidBlockHeight,
              signature,
            })

            if (receipt.value.err === null) {
              return {
                status: "success",
                hash: signature,
              }
            }

            const error = Error(svmErrorToString(receipt.value.err))
            return {
              status: "failed",
              hash: signature,
              error: new TransactionRevertedError(error),
            }
          } catch (error) {
            return {
              status: "failed",
              hash: signature,
              error: parseSvmTransactionError(error),
            }
          }
        }

        return {
          hash: signature,
          wait,
        }
      } catch (error) {
        throw parseSvmTransactionError(error)
      }
    },
    [connection, wallet],
  )

  return { transact }
}

function svmErrorToString(err: TransactionError): string {
  if (typeof err === "string") {
    return err
  }

  if (typeof err === "object") {
    try {
      return JSON.stringify(err)
    } catch {
      return String(err)
    }
  }

  return String(err)
}
